import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IBulkTransaction, defaultValue } from 'app/shared/model/bulk-transaction.model';

const initialState: EntityState<IBulkTransaction> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/bulk-transactions';
const bankStaffApiUrl = 'api/bulk-transactions/bankStaff';

// Actions

export const getEntities = createAsyncThunk('bulkTransaction/fetch_entity_list', async ({ page, size, sort, search }: IQueryParams) => {
  const requestUrl = `${apiUrl}${
    sort ? `?page=${page}&size=${size}&sort=${sort}&search=${search}&` : '?'
  }cacheBuster=${new Date().getTime()}`;
  return axios.get<IBulkTransaction[]>(requestUrl);
});

export const getEntitiesByBankStaff = createAsyncThunk(
  'bulkTransaction/fetch_entity_list',
  async ({ page, size, sort, search, createdBy }: IQueryParams) => {
    const requestUrl = `${bankStaffApiUrl}/${createdBy}${
      sort ? `?page=${page}&size=${size}&sort=${sort}&search=${search}&` : '?'
    }cacheBuster=${new Date().getTime()}`;
    return axios.get<IBulkTransaction[]>(requestUrl);
  }
);

export const getEntity = createAsyncThunk(
  'bulkTransaction/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IBulkTransaction>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'bulkTransaction/create_entity',
  async (entity: IBulkTransaction, thunkAPI) => {
    const result = await axios.post<IBulkTransaction>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'bulkTransaction/update_entity',
  async (entity: IBulkTransaction, thunkAPI) => {
    const result = await axios.put<IBulkTransaction>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'bulkTransaction/partial_update_entity',
  async (entity: IBulkTransaction, thunkAPI) => {
    const result = await axios.patch<IBulkTransaction>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'bulkTransaction/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IBulkTransaction>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const BulkTransactionSlice = createEntitySlice({
  name: 'bulkTransaction',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities, getEntitiesByBankStaff), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = BulkTransactionSlice.actions;

// Reducer
export default BulkTransactionSlice.reducer;
